import { type ReactElement } from 'react'
import { isNotEmpty } from '@terros/common'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Divider, Text } from '@mantine/core'
import { useQueryDisclosure, useToastError } from '@hooks'
import { Size, SizePrimitives, useQuizFeature, useQuizzes, useTranslation } from '@front/model'
import { ErrorScreen, HeroImage, MaterialSymbolIcon, NotReady, Page, TButton, TStack } from '@components'
import { QuizItem } from './QuizItem'
import { AddQuiz } from './AddQuiz'

function QuizzesPage(): ReactElement {
  const [isDrawerOpen, { open: openDrawer, close: closeDrawer }] = useQueryDisclosure('add')
  const { data: quizzes = [], refetch, isFetching, isLoading, error } = useQuizzes()

  useToastError(error)

  const { t } = useTranslation()
  const { milestone, isLoading: isLoadingFeature, isEnabled } = useQuizFeature()

  if (isLoading || isLoadingFeature)
    return <NotReady data={quizzes} error={error} id='Quizzes' isLoading={isLoading || isLoadingFeature} />
  if (!isEnabled) return <ErrorScreen message='This feature is not available.' />

  const quizzesExist = isNotEmpty(quizzes)

  return (
    <Page>
      <Page.Heading isFetching={isFetching} milestone={milestone} refetch={refetch} title={t('Quizzes')}>
        {quizzesExist && (
          <TButton icon='add' onClick={openDrawer}>
            Add Quiz
          </TButton>
        )}
        <TButton component={Link} icon='archive' to='trash' tooltip={t('View Trash')} variant='secondary' />
      </Page.Heading>
      <Page.Content scrollArea>
        {quizzesExist ? (
          <TStack divider={<Divider />} gap={Size.gap.list}>
            {quizzes.map((quiz) => (
              <QuizItem key={quiz.quizId} quiz={quiz} />
            ))}
          </TStack>
        ) : (
          <HeroImage>
            <MaterialSymbolIcon icon='checklist' size={SizePrimitives['12']} />
            <Text c='dimmed' size='lg'>
              No Quizzes Yet
            </Text>
            <TButton icon='add' onClick={openDrawer}>
              Add Quiz
            </TButton>
          </HeroImage>
        )}
      </Page.Content>

      <AddQuiz opened={isDrawerOpen} onClose={closeDrawer} />
    </Page>
  )
}

export const Route = createFileRoute('/quiz/')({
  component: QuizzesPage,
})
